import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSelfCanonical } from "@/lib/seo";
import { MarketingNav } from "@/components/MarketingNav";
import { MarketingFooter } from "@/components/MarketingFooter";
import { Heart, ShieldCheck, FileText, Mic, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1576765607924-3f7b8410a787?w=1600&q=80&auto=format&fit=crop";

const FEATURES = [
  { icon: Mic, slug: "ambient-scribe", title: "Ambient visit scribe", body: "Record any doctor visit — AI drafts the diagnosis, meds, follow-ups and family tasks instantly." },
  { icon: FileText, slug: "claims-appeals", title: "Claims & appeals engine", body: "Upload an EOB. We flag overcharges and draft an ERISA-aware appeal letter for your review." },
  { icon: Heart, slug: "care-circle", title: "Unified care circle", body: "Meds, vitals, appointments and aide shifts — one hub the whole family stays in sync on." },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useSelfCanonical("KinBridge Health · Care coordination for families");
  return (
    <div className="min-h-screen bg-surface">
      {/* Hero */}
      <div className="relative h-[440px] overflow-hidden bg-brand-dark">
        <img src={HERO} alt="" aria-hidden="true" onError={(e) => { e.currentTarget.style.display = "none"; }} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(105deg, rgba(6,33,72,0.97) 0%, rgba(6,33,72,0.86) 48%, rgba(10,47,95,0.5) 100%)" }} />
        <div className="relative max-w-5xl mx-auto px-6 h-full flex flex-col">
          <MarketingNav />
          <div className="flex-1 flex flex-col justify-center max-w-2xl kb-fade-up pb-12">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]">
              The operating system for family care & medical advocacy
            </h1>
            <p className="text-mint-tint text-lg mt-5 max-w-xl">
              Your family&apos;s advocate inside the healthcare system. Coordinate care, fight overcharges, and keep everyone in sync.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <button
                onClick={() => navigate(user ? "/app" : "/signup")}
                data-testid="landing-get-started"
                className="inline-flex items-center gap-2 bg-mint text-brand-dark font-semibold rounded-full px-6 py-3 hover:bg-white transition-colors"
              >
                {user ? "Go to your circle" : "Start your care circle"} <ArrowRight className="w-4 h-4" />
              </button>
              <Link
                to="/login"
                data-testid="landing-login"
                className="inline-flex items-center gap-2 bg-white/10 text-white font-semibold rounded-full px-6 py-3 border border-white/25 hover:bg-white/20 transition-colors"
              >
                I&apos;m a caregiver — sign in
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="grid sm:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <Link key={f.title} to={`/features/${f.slug}`} data-testid={`landing-feature-${f.slug}`} className="group bg-white border border-line rounded-2xl p-6 hover:-translate-y-1 transition-transform duration-200">
              <div className="w-12 h-12 rounded-xl bg-mint-tint flex items-center justify-center mb-4">
                <f.icon className="w-6 h-6 text-mint-dark" />
              </div>
              <h3 className="font-display text-xl text-ink">{f.title}</h3>
              <p className="text-sm text-subtle mt-2 leading-relaxed">{f.body}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand group-hover:gap-2 transition-all">Learn more <ArrowRight className="w-3.5 h-3.5" /></span>
            </Link>
          ))}
        </div>

        <div className="mt-14 bg-brand-dark rounded-3xl p-10 text-center relative overflow-hidden">
          <div className="relative">
            <ShieldCheck className="w-10 h-10 text-mint mx-auto mb-4" />
            <h2 className="font-display text-3xl text-white">HIPAA-conscious by design</h2>
            <p className="text-mint-tint mt-3 max-w-lg mx-auto">
              Zero-persistence audio, encrypted document storage, and no PHI stored with payments. Built for working caregivers.
            </p>
            <button
              onClick={() => navigate("/signup")}
              data-testid="landing-cta-bottom"
              className="mt-7 inline-flex items-center gap-2 bg-mint text-brand-dark font-semibold rounded-full px-7 py-3 hover:bg-white transition-colors"
            >
              Create a caregiver account <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-subtle mt-10">
          KinBridge Health · HIPAA-conscious · Cancel anytime ·{" "}
          <Link to="/legal" data-testid="landing-legal-link" className="text-brand font-semibold hover:underline">Legal &amp; privacy</Link>
        </p>
      </div>

      <MarketingFooter />
    </div>
  );
}
